import { SerializedError } from '@reduxjs/toolkit';
import {
  BaseQueryError,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';

export interface CollectionModel<T> {
  items: T[];
}

export interface CollectionFilteredModel<T> extends CollectionModel<T> {
  countAll: number;
  countFilter: number;
}

// initialize an empty api service that we'll inject endpoints into later as needed

const baseQuery = fetchBaseQuery({
  baseUrl: '/',
  prepareHeaders: (headers, { getState }) => {
    headers.set('X-CSRF', '1');
    return headers;
  },
});

export type ApiBaseQueryFn = BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError | SerializedError | undefined | unknown
>;

export type ApiBaseQueryError = BaseQueryError<ApiBaseQueryFn>;

const baseQueryWithMaintenanceMode: ApiBaseQueryFn = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);
  if (result.error) {
    if (result.error.status === 503) {
      // Maintenance mode
      console.debug('maintenance mode');
      api.dispatch({ type: 'auth/inMaintenance' });
    } else if (result.error.status === 401 && args !== 'api/me') {
      // Token has expired - redirect to login flow and attempt to come back to current location
      console.debug('token expired');
      window.location.href = `/auth/login?returnUrl=${window.location.pathname}`;
    }
  }
  return result;
};

export enum QueryTag {
  // SiteCache: The tag for all queries that are only relevant to the current site.
  SiteCache = 'SiteCache',
  // TenantCache: The tag for all queries that can be shared across sites.
  TenantCache = 'TenantCache',
  // All other tags are used to cache specific queries or groups of queries.
  Asset = 'Asset',
  AssetActivity = 'AssetActivity',
  AssetFaults = 'AssetFaults',
  AssetGeofenceInteractions = 'AssetGeofenceInteractions',
  AssetInteractions = 'AssetInteractions',
  AssetInteractionsSummary = 'AssetInteractionsSummary',
  AssetSelfTestSummary = 'AssetSelfTestSummary',
  AssetVehicleInteractions = 'AssetVehicleInteractions',
  ConfigGroup = 'ConfigGroup',
  Document = 'Document',
  Geofence = 'Geofence',
  Health = 'Health',
  Interaction = 'Interaction',
  InteractionsSummary = 'InteractionsSummary',
  Metadata = 'Metadata',
  Notification = 'Notification',
  ScenariosStat = 'ScenariosStat',
  ScenariosTimeSeries = 'ScenariosTimeSeries',
  UserAuth = 'UserAuth',
  Heatmap = 'Heatmap',
  Investigation = 'Investigation',
  InvestigationNotes = 'InvestigationNotes',
  InvestigationTracks = 'InvestigationTracks',
  InvestigationRawTracks = 'InvestigationRawTracks',
}

const tagTypes: QueryTag[] = [...Object.values(QueryTag)];

export const customerApi = createApi({
  baseQuery: baseQueryWithMaintenanceMode,
  tagTypes,
  endpoints: () => ({}),
});
